import {inject, Injectable, OnDestroy} from '@angular/core';
import {CommonBuilderDataService} from '../../builder-v2/services/common-builder-data.service';
import {Config} from '../../config/config';
import {UserSubscription} from '../../user/user-subscription';
import {Store} from '@ngrx/store';
import * as fromStore from '../../store';
import {Subscription} from 'rxjs';

/**
 * Options for vibecoding flow detection
 */
export interface VibeCodingFlowOptions {
  /**
   * Override mobile detection (optional)
   * If not provided, uses window.innerWidth
   */
  isMobile?: boolean;
}

/**
 * Parsed experiment cookie data
 */
export interface ExperimentData {
  vibeCodingVersion?: string;
  vibeCodingMobileVersion?: string;
  ecommerceVibeCodingVersion?: string;
  [key: string]: any;
}

/**
 * Service to centralize vibecoding flow detection logic.
 * Eliminates duplicated cookie/product_id checks across components.
 * Automatically retrieves user subscription from the store.
 */
@Injectable({providedIn: 'root'})
export class VibeCodingFlowService implements OnDestroy {
  private _commonDataService = inject(CommonBuilderDataService);
  private _store = inject(Store<fromStore.DbStoreState>);

  private _userSubscription: UserSubscription | null = null;
  private _subscription: Subscription = new Subscription();

  constructor() {
    // Subscribe to user subscription from store
    this._subscription.add(
      this._store.select(fromStore.getUserSubscription).subscribe(
        (userSubscription) => {
          this._userSubscription = userSubscription;
        }
      )
    );
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  /**
   * Check if desktop vibecoding flow is active
   *
   * @param options - Configuration options
   * @returns true if desktop vibecoding flow is active
   */
  isVibeCodingFlow(options: VibeCodingFlowOptions = {}): boolean {
    return (
      ( ['wvc', 'both'].includes(this._userSubscription?.builderType) && (localStorage.getItem('builderType') === 'wvc')) ||
      (!this._userSubscription && (localStorage.getItem('builderType') === 'wvc'))
    );
  }

  /**
   * Check if combined outline (desktop OR mobile vibecoding) is active
   * Also sets the global combinedOutline flag in CommonBuilderDataService
   *
   * @param options - Configuration options
   * @returns true if either desktop or mobile vibecoding flow is active
   */
  isCombinedOutline(options: VibeCodingFlowOptions = {}): boolean {
    const combined = this.isVibeCodingFlow(options);

    // Auto-set global flag (side effect)
    this._commonDataService.combinedOutline = combined;

    return combined;
  }

  /**
   * Check if current screen is mobile
   *
   * @returns true if screen width <= 991px
   */
  isMobile(): boolean {
    return window.innerWidth <= 991;
  }
}
